import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { translateRoomType } from '../../../constants/roomTypes';
import NotificationModal from './NotificationModal';
import { roomService } from '../../../services/api';
import { authService } from '../../../services/api';

/** dd/MM/yyyy para exibição (quando source === 'landing') */
const formatDateDDMMYYYY = (isoStr) => {
  if (!isoStr || typeof isoStr !== 'string') return '';
  const [y, m, d] = isoStr.split('-');
  if (!y || !m || !d) return isoStr;
  return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
};

/** dd/MM/yyyy ou dígitos → ISO yyyy-MM-dd */
const parseDDMMYYYYToISO = (str) => {
  if (!str || typeof str !== 'string') return '';
  const digits = str.replace(/\D/g, '');
  if (digits.length !== 8) return '';
  const d = digits.slice(0, 2);
  const m = digits.slice(2, 4);
  const y = digits.slice(4, 8);
  const day = parseInt(d, 10);
  const month = parseInt(m, 10);
  const year = parseInt(y, 10);
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) return '';
  return `${y}-${m}-${d}`;
};

/** Máscara em tempo real: dd/MM/yyyy */
const maskDateInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

/**
 * Calcula quantas crianças são efetivamente cobradas, com base na idade mínima.
 */
const computeChargeableChildren = (childrenCount, childAges, minChildChargeAge) => {
  if (!childrenCount || childrenCount <= 0) return 0;
  if (minChildChargeAge == null || !Number.isFinite(Number(minChildChargeAge)) || Number(minChildChargeAge) <= 0) {
    return 0;
  }
  const ages = Array.isArray(childAges) ? childAges : [];
  return ages
    .slice(0, childrenCount)
    .map((a) => Number(a))
    .filter((a) => Number.isFinite(a) && a > Number(minChildChargeAge)).length;
};

/**
 * Regra de composição do preço por noite:
 * - 1 pessoa  → preço solteiro
 * - 2 pessoas → preço casal (ou solteiro + criança com desconto, quando 1 adulto + 1 criança cobrada)
 * - 3+ pessoas → preço casal + (extras × preço solteiro)
 */
const computeNightlyPrice = ({
  singlePrice,
  couplePrice,
  adults,
  chargeableChildren,
  childDiscountPercent
}) => {
  const couple = Number(couplePrice) || 0;
  const single = Number(singlePrice) > 0 ? Number(singlePrice) : couple;
  const adultsNum = Math.max(0, Number(adults) || 0);
  const chargedChildren = Math.max(0, Number(chargeableChildren) || 0);

  const childMultiplier = childDiscountPercent != null && Number.isFinite(Number(childDiscountPercent))
    ? (100 - Number(childDiscountPercent)) / 100
    : 1;
  const childSingle = single * childMultiplier;

  const totalPeople = adultsNum + chargedChildren;

  if (totalPeople <= 1) {
    return single;
  }

  if (totalPeople === 2) {
    if (adultsNum >= 2) return couple;
    if (adultsNum === 1 && chargedChildren === 1) return single + childSingle;
    return couple;
  }

  // 3+ pessoas
  if (adultsNum >= 2) {
    const extraAdults = adultsNum - 2;
    return couple + (extraAdults * single) + (chargedChildren * childSingle);
  }
  // 1 adulto + várias crianças cobradas
  return single + (chargedChildren * childSingle);
};

const ReservationModal = React.memo(({ isOpen, onClose, reservation, onSave, mode = 'view', source = 'dashboard', hotel: hotelProp = null }) => {
  const isLanding = source === 'landing';
  const getHotel = () => (isLanding && hotelProp ? hotelProp : authService.getSelectedHotel?.() ?? null);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    originCity: '',
    documentNumber: '',
    roomId: '',
    roomNumber: '',
    roomType: '',
    checkInDate: '',
    checkInTime: '14:00', // Horário padrão de check-in
    checkOutDate: '',
    checkOutTime: '12:00', // Horário padrão de check-out
    adults: 1,
    children: 0,
    childAges: [],
    specialRequests: '',
    totalAmount: 0,
    discountAmount: 0, // Desconto aplicado
    status: 'PENDING',
    paymentStatus: 'PENDING',
    hotelId: null
  });

  const [availableRooms, setAvailableRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const roomOptions = useMemo(() => {
    const options = filteredRooms.map(room => ({
      value: room.id.toString(),
      label: room.label || `Quarto ${room.number} - ${translateRoomType(room.type)}`
    }));

    if (mode === 'edit' && reservation) {
      const currentRoomId = reservation.roomId?.toString() || (reservation.room?.id || reservation.room)?.toString();
      if (currentRoomId) {
        const hasCurrentRoom = options.some(opt => opt.value === currentRoomId);
        if (!hasCurrentRoom) {
          const roomNumber = reservation.roomNumber || reservation.room?.number || 'Atual';
          const roomType = reservation.roomType || reservation.room?.type || '';
          options.unshift({
            value: currentRoomId,
            label: `Quarto ${roomNumber} - ${translateRoomType(roomType)} (Quarto Atual)`
          });
        }
      }
    }
    
    return options;
  }, [filteredRooms, mode, reservation]);
  const [loading, setLoading] = useState(false);
  const [minChildChargeAge, setMinChildChargeAge] = useState(null);
  const [childDiscountPercent, setChildDiscountPercent] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    type: 'success',
    title: '',
    message: '',
    details: null
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPriceDetails, setShowPriceDetails] = useState(false);
  const [isPreReservation, setIsPreReservation] = useState(false);
  /** Exibição dd/MM/yyyy durante edição no modo landing (checkIn/checkOut ou null = usar valor do form) */
  const [dateDisplayLanding, setDateDisplayLanding] = useState({ checkIn: null, checkOut: null });

  const steps = [
    { id: 1, title: 'Dados da Estadia', description: 'Selecione as datas de check-in e check-out' },
    { id: 2, title: 'Seleo do Quarto', description: 'Escolha o quarto ideal para sua estadia' },
    { id: 3, title: 'Dados do Hóspede', description: 'Informações pessoais do hóspede' },
    { id: 4, title: 'Detalhes da Reserva', description: 'Finalizar e confirmar reserva' }
  ];

  // Resetar formData quando o modal abrir em modo create
  useEffect(() => {
    if (isOpen && mode === 'create') {
      console.log(' ReservationModal: Resetando formulrio para modo create');
      console.log(' Dados de reservation recebidos:', reservation);
      
      setCurrentStep(1);
      
      // Se recebeu dados pr-preenchidos (ex: datas selecionadas), usar eles
      // Caso contrrio, usar valores vazios
      setFormData({
        guestName: reservation?.guestName || '',
        guestEmail: reservation?.guestEmail || '',
        guestPhone: reservation?.guestPhone || '',
        originCity: reservation?.originCity || '',
        documentNumber: reservation?.documentNumber || '',
        roomId: reservation?.roomId || '',
        roomNumber: reservation?.roomNumber || '',
        roomType: reservation?.roomType || '',
        checkInDate: reservation?.checkInDate || '',
        checkInTime: reservation?.checkInTime || '14:00',
        checkOutDate: reservation?.checkOutDate || '',
        checkOutTime: reservation?.checkOutTime || '12:00',
        adults: reservation?.adults || 1,
        children: reservation?.children || 0,
        childAges: Array.from({ length: reservation?.children || 0 }, () => ''),
        specialRequests: reservation?.specialRequests || '',
        totalAmount: reservation?.totalAmount || 0,
        discountAmount: reservation?.discountAmount || 0,
        status: 'PENDING',
        paymentStatus: 'PENDING',
        hotelId: reservation?.hotelId || null
      });
      setFieldErrors({});
      setShowNotification(false);
      if (isLanding) setDateDisplayLanding({ checkIn: null, checkOut: null });
    }
  }, [isOpen, mode, reservation, isLanding]);

  // Carregar configurações de idade mínima e desconto percentual para cobrança de criança (Dashboard via API; LP via hotel da resposta pública)
  useEffect(() => {
    if (!isOpen) return;
    if (isLanding && hotelProp) {
      const min = hotelProp.minChildChargeAge;
      const disc = hotelProp.childDiscountPercent;
      const minNum = (min != null && min !== '' && Number.isFinite(Number(min)) && Number(min) > 0) ? Number(min) : null;
      const discNum = (disc != null && disc !== '' && Number.isFinite(Number(disc)) && Number(disc) >= 0) ? Number(disc) : null;
      setMinChildChargeAge(minNum);
      setChildDiscountPercent(discNum);
      return;
    }
    const selectedHotel = getHotel();
    if (selectedHotel?.id) {
      import('../../../services/api').then(({ hotelService }) => {
        Promise.all([
          hotelService.getSetting(selectedHotel.id, 'MIN_CHILD_CHARGE_AGE').catch(() => null),
          hotelService.getSetting(selectedHotel.id, 'CHILD_DISCOUNT_PERCENT').catch(() => null)
        ]).then(([minAgeResp, discountResp]) => {
          const minAgeVal = minAgeResp?.value != null && minAgeResp.value !== '' ? Number(minAgeResp.value) : null;
          const discountVal = discountResp?.value != null && discountResp.value !== '' ? Number(discountResp.value) : null;
          const minNum = (minAgeVal != null && Number.isFinite(minAgeVal) && minAgeVal > 0) ? minAgeVal : null;
          const discNum = (discountVal != null && Number.isFinite(discountVal) && discountVal >= 0) ? discountVal : null;
          setMinChildChargeAge(minNum);
          setChildDiscountPercent(discNum);
        });
      });
    }
  }, [isOpen, isLanding, hotelProp]);

  // Atualizar formData quando reservation mudar
  useEffect(() => {
    if (reservation && mode !== 'create') {
      
      setFormData({
        guestName: reservation.guestName || '',
        guestEmail: reservation.guestEmail || '',
        guestPhone: reservation.guestPhone || '',
        originCity: reservation.originCity || '',
        documentNumber: reservation.documentNumber || '',
        roomId: reservation.roomId || '',
        roomNumber: reservation.room?.number || reservation.roomNumber || '',
        roomType: reservation.room?.type?.name || reservation.roomType || '',
        room: reservation.room || null,
        checkInDate: reservation.checkInDate ? new Date(reservation.checkInDate).toISOString().split('T')[0] : '',
        checkInTime: reservation.checkInDate ? new Date(reservation.checkInDate).toTimeString().slice(0, 5) : '14:00',
        checkOutDate: reservation.checkOutDate ? new Date(reservation.checkOutDate).toISOString().split('T')[0] : '',
        checkOutTime: reservation.checkOutDate ? new Date(reservation.checkOutDate).toTimeString().slice(0, 5) : '12:00',
        adults: reservation.adults || 1,
        children: reservation.children || 0,
        specialRequests: reservation.specialRequests || '',
        totalAmount: reservation.totalAmount 
          ? (typeof reservation.totalAmount === 'string' 
            ? parseFloat(reservation.totalAmount.replace(',', '.')) 
            : Number(reservation.totalAmount)).toFixed(2).replace('.', ',')
          : '',
        discountAmount: reservation.discountAmount 
          ? (typeof reservation.discountAmount === 'string' 
            ? parseFloat(reservation.discountAmount.replace(',', '.')) 
            : Number(reservation.discountAmount)).toFixed(2).replace('.', ',')
          : '',
        status: reservation.status || 'PENDING',
        paymentStatus: reservation.paymentStatus || 'PENDING',
        hotelId: reservation.hotelId || null
      });
    }
  }, [reservation, mode]);

  const loadAvailableRooms = useCallback(async () => {
    try {
      setLoading(true);
      const selectedHotel = getHotel();
      if (!selectedHotel?.id) {
        if (!isLanding) showNotificationModal('error', 'Hotel Não Selecionado', 'Selecione um hotel no topo para criar uma reserva.');
        return;
      }
      if (isLanding && hotelProp?.rooms) {
        const formattedRooms = hotelProp.rooms.map(room => ({
          ...room,
          value: room.id,
          label: `Quarto ${room.number} - ${translateRoomType(room.type)} (Solteiro: R$ ${room.singlePrice ?? 0}, Casal: R$ ${room.couplePrice ?? 0})`
        }));
        setAvailableRooms(formattedRooms);
        const available = formattedRooms.filter(r => !r.status || r.status === 'AVAILABLE');
        setFilteredRooms(available.length ? available : formattedRooms);
        setLoading(false);
        return;
      }
      const rooms = await roomService.getAll({ hotelId: selectedHotel.id });
        
                 if (!Array.isArray(rooms)) {
           showNotificationModal('error', 'Erro na API', 'Erro: API no retornou dados vlidos de quartos');
           return;
         }
        
                 if (rooms.length === 0) {
           showNotificationModal('warning', 'Nenhum Quarto Encontrado', 'Nenhum quarto encontrado para este hotel. Verifique se existem quartos cadastrados.');
           setAvailableRooms([]);
           setFilteredRooms([]);
           return;
         }
      
      // No filtrar por status - todos os quartos sero considerados inicialmente
      // A disponibilidade real ser verificada pelo filtro de datas
      const availableRoomsList = rooms;
      
      const formattedRooms = availableRoomsList.map(room => ({
        ...room,
        value: room.id.toString(),
        label: `Quarto ${room.number} - ${translateRoomType(room.type)} (Solteiro: R$ ${room.singlePrice || room.couplePrice * 0.7}, Casal: R$ ${room.couplePrice})`
      }));
      
      setAvailableRooms(formattedRooms);
      
      // Inicializar filteredRooms vazio - ser preenchido pelo filtro de datas
      setFilteredRooms([]);
      
         } catch (error) {
      console.error('Erro ao carregar quartos:', error);
       showNotificationModal('error', 'Erro ao Carregar Quartos', 'Erro ao carregar quartos disponveis. Tente novamente.');
     } finally {
      setLoading(false);
    }
  }, []);

  // Filtrar quartos quando as datas ou horrios mudarem
  useEffect(() => {
    if (formData.checkInDate && formData.checkOutDate && formData.checkInTime && formData.checkOutTime && availableRooms.length > 0) {
      // Usar IIFE para chamar funo async
      (async () => {
        await filterRoomsByDates();
      })();
    } else if (formData.checkInDate && formData.checkOutDate && formData.checkInTime && formData.checkOutTime && availableRooms.length === 0) {
      // Se as datas e horrios esto selecionados mas no h quartos, carregar quartos primeiro
      loadAvailableRooms();
    }
  }, [formData.checkInDate, formData.checkInTime, formData.checkOutDate, formData.checkOutTime, availableRooms, loadAvailableRooms]);

  // Carregar quartos disponveis quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      loadAvailableRooms();
    }
  }, [isOpen, loadAvailableRooms]);
  
  // Executar filtro quando filteredRooms estiver vazio mas availableRooms no
  useEffect(() => {
    if (filteredRooms.length === 0 && availableRooms.length > 0 && formData.checkInDate && formData.checkOutDate && formData.checkInTime && formData.checkOutTime) {
      (async () => {
        await filterRoomsByDates();
      })();
    }
  }, [filteredRooms.length, availableRooms.length, formData.checkInDate, formData.checkInTime, formData.checkOutDate, formData.checkOutTime]);

  // Recalcular totalAmount (valor por noite) sempre que o quarto ou a composição
  // de hóspedes mudar no modo de criação. Em modos view/edit respeitamos o valor salvo.
  useEffect(() => {
    if (mode !== 'create') return;
    if (!formData.roomId || availableRooms.length === 0) return;
    const selectedRoom = availableRooms.find((r) => String(r.id) === String(formData.roomId));
    if (!selectedRoom) return;

    const adults = Number(formData.adults) || 1;
    const childrenCount = Number(formData.children) || 0;
    const chargeableChildren = computeChargeableChildren(childrenCount, formData.childAges, minChildChargeAge);

    const calculatedPrice = computeNightlyPrice({
      singlePrice: selectedRoom.singlePrice,
      couplePrice: selectedRoom.couplePrice,
      adults,
      chargeableChildren,
      childDiscountPercent
    });

    setFormData((prev) => (prev.totalAmount === calculatedPrice ? prev : { ...prev, totalAmount: calculatedPrice }));
  }, [mode, formData.roomId, formData.adults, formData.children, formData.childAges, availableRooms, minChildChargeAge, childDiscountPercent]);
  
  // Funo global para debug (disponvel no console do browser) - removido para performance

  const filterRoomsByDates = async () => {
    if (!formData.checkInDate || !formData.checkOutDate || !formData.checkInTime || !formData.checkOutTime) {
      console.log(' filterRoomsByDates: Datas ou horrios no selecionados ainda');
      return;
    }
    
    console.log(' filterRoomsByDates: Filtrando quartos para perodo:', {
      checkIn: `${formData.checkInDate} ${formData.checkInTime}`,
      checkOut: `${formData.checkOutDate} ${formData.checkOutTime}`
    });
    console.log(' filterRoomsByDates: availableRooms.length:', availableRooms.length);
    console.log(' filterRoomsByDates: availableRooms data:', availableRooms);
    
    try {
      setLoading(true);
      
      const selectedHotel = getHotel();
      if (!selectedHotel?.id) {
        console.log(' filterRoomsByDates: Nenhum hotel selecionado');
        setFilteredRooms([]);
        return;
      }
      if (isLanding && hotelProp?.rooms) {
        const available = availableRooms.filter(r => !r.status || r.status === 'AVAILABLE');
        setFilteredRooms(available.length ? available : availableRooms);
        setLoading(false);
        return;
      }
      
      // Criar objetos Date com horrios especficos
      const checkInDateTime = new Date(`${formData.checkInDate}T${formData.checkInTime}`);
      const checkOutDateTime = new Date(`${formData.checkOutDate}T${formData.checkOutTime}`);
      
      console.log(' filterRoomsByDates: Check-in DateTime:', checkInDateTime.toISOString());
      console.log(' filterRoomsByDates: Check-out DateTime:', checkOutDateTime.toISOString());
      
      // Validar datas e horrios
      if (isNaN(checkInDateTime.getTime()) || isNaN(checkOutDateTime.getTime())) {
        console.error(' filterRoomsByDates: Datas/horários inválidos:', { 
          checkIn: `${formData.checkInDate} ${formData.checkInTime}`, 
          checkOut: `${formData.checkOutDate} ${formData.checkOutTime}` 
        });
        setFilteredRooms([]);
        return;
      }
      
      if (checkInDateTime >= checkOutDateTime) {
        console.error(' filterRoomsByDates: Check-in deve ser anterior ao check-out');
        setFilteredRooms([]);
        return;
      }
      
      // Verificar se o período  de pelo menos 1 hora
      const timeDiff = checkOutDateTime.getTime() - checkInDateTime.getTime();
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      if (hoursDiff < 1) {
        console.error(' filterRoomsByDates: Período deve ser de pelo menos 1 hora');
        showNotificationModal('error', 'Período Inválido', 'O período de estadia deve ser de pelo menos 1 hora.');
        setFilteredRooms([]);
        return;
      }
      
      const datesToCheck = [];
      
      // Gerar array de datas para verificar (incluindo check-in, excluindo check-out)
      for (let d = new Date(checkInDateTime); d < checkOutDateTime; d.setDate(d.getDate() + 1)) {
        datesToCheck.push(new Date(d).toISOString().split('T')[0]);
      }
      

      
      console.log(' filterRoomsByDates: Datas para verificar:', datesToCheck);
      
      // Verificar disponibilidade para cada data
      const availabilityPromises = datesToCheck.map(date => 
        roomService.getAvailability(date, selectedHotel.id)
      );
      
      const availabilityResults = await Promise.all(availabilityPromises);
      console.log(' filterRoomsByDates: Resultados de disponibilidade:', availabilityResults);
      
      // Debug detalhado dos resultados
      availabilityResults.forEach((dayAvailability, index) => {
        const date = datesToCheck[index];
        console.log(` filterRoomsByDates: Disponibilidade para ${date}:`, {
          totalRooms: dayAvailability.length,
          occupiedRooms: dayAvailability.filter(r => r.statusForDate === 'OCCUPIED').length,
          availableRooms: dayAvailability.filter(r => r.statusForDate === 'AVAILABLE').length,
          sampleRoom: dayAvailability[0]
        });
      });
      
      // Criar mapa de quartos disponveis para todo o perodo
      const roomAvailabilityMap = new Map();
      
      // Inicializar todos os quartos como disponveis
      availableRooms.forEach(room => {
        const roomId = room.id;
        const roomIdStr = roomId.toString();
        roomAvailabilityMap.set(roomId, true);
        roomAvailabilityMap.set(roomIdStr, true);
        console.log(` filterRoomsByDates: Inicializando quarto ${roomId} (${room.number}) como disponvel - ID original e string`);
      });
      
      // Verificar se cada quarto est disponvel para todas as datas
      availabilityResults.forEach((dayAvailability, index) => {
        const date = datesToCheck[index];
        console.log(` filterRoomsByDates: Verificando disponibilidade para ${date}:`, dayAvailability);
        
        dayAvailability.forEach(roomAvailability => {
          // Se o quarto est ocupado OU reservado em qualquer data do perodo, marcar como indisponvel
          if (roomAvailability.statusForDate === 'OCCUPIED' || roomAvailability.statusForDate === 'RESERVED') {
            const status = roomAvailability.statusForDate === 'OCCUPIED' ? 'ocupado' : 'reservado';
            console.log(` filterRoomsByDates: Quarto ${roomAvailability.roomId} ${status} em ${date}`);
            console.log(` filterRoomsByDates: Tipo do ID: ${typeof roomAvailability.roomId}, Valor: ${roomAvailability.roomId}`);
            
            // Marcar como indisponvel no mapa - usar string para garantir compatibilidade
            const roomIdStr = roomAvailability.roomId.toString();
            roomAvailabilityMap.set(roomIdStr, false);
            roomAvailabilityMap.set(roomAvailability.roomId, false);
            
            // Tambm marcar por nmero do quarto para garantir compatibilidade
            const roomNumber = roomAvailability.number;
            if (roomNumber) {
              roomAvailabilityMap.set(roomNumber, false);
            }
            
            console.log(` filterRoomsByDates: Marcando quarto ${roomIdStr} (${roomNumber}) como indisponível (${status})`);
          }
        });
      });
      
      // Filtrar quartos que esto disponveis para todo o perodo
      console.log(' filterRoomsByDates: Mapa de disponibilidade final:', {
        mapSize: roomAvailabilityMap.size,
        mapEntries: Array.from(roomAvailabilityMap.entries())
      });
      
      const availableForPeriod = availableRooms.filter(room => {
        // Verificar disponibilidade usando tanto o ID original quanto como string
        const roomId = room.id;
        const roomIdStr = roomId.toString();
        const isAvailable = roomAvailabilityMap.get(roomId) === true && roomAvailabilityMap.get(roomIdStr) === true;
        
        console.log(` filterRoomsByDates: Quarto ${roomId} (${room.number}) - ID original: ${roomAvailabilityMap.get(roomId)}, ID string: ${roomAvailabilityMap.get(roomIdStr)}, disponvel: ${isAvailable}`);
        return isAvailable;
      });
      
      console.log(' filterRoomsByDates: Quartos disponíveis para todo o período:', availableForPeriod.length);
      console.log(' filterRoomsByDates: Dados dos quartos disponíveis:', availableForPeriod);
      
      // Se no h quartos disponveis para o perodo, mostrar lista vazia
      if (availableForPeriod.length === 0) {
        console.log(' filterRoomsByDates: Nenhum quarto disponível para o período selecionado');
        console.log(' filterRoomsByDates: Mostrando lista vazia para indicar indisponibilidade');
        setFilteredRooms([]);
        
        // Mostrar notificao para o usurio
        showNotificationModal('warning', 'Nenhum Quarto Disponível', 
          `Não há quartos disponíveis para o período de ${formData.checkInDate} a ${formData.checkOutDate}. Tente outras datas ou entre em contato com a recepção.`);
      } else {
        setFilteredRooms(availableForPeriod);
      }
      
    } catch (error) {
      console.error(' filterRoomsByDates: Erro ao verificar disponibilidade:', error);
      // Em caso de erro, mostrar lista vazia para evitar confuso
      setFilteredRooms([]);
    } finally {
      setLoading(false);
    }
  };

  // Funo para formatar valor monetrio
  const formatCurrencyInput = (value) => {
    if (!value) return '';
    
    // Remove tudo exceto nmeros
    const numbers = value.replace(/\D/g, '');
    
    if (numbers === '') return '';
    
    // Se comear com zero e tiver mais nmeros, remove o zero inicial
    let cleanedNumbers = numbers;
    if (cleanedNumbers.length > 1 && cleanedNumbers[0] === '0') {
      cleanedNumbers = cleanedNumbers.substring(1);
    }
    
    // Converte para nmero e divide por 100 para ter centavos
    const numberValue = parseFloat(cleanedNumbers) / 100;
    
    // Formata com 2 casas decimais e substitui ponto por vrgula
    return numberValue.toFixed(2).replace('.', ',');
  };

  const handleInputChange = (field, value) => {
    // Máscara e sanitização de CPF/CNPJ no front; armazenar mascarado, enviar só dígitos no save
    if (field === 'documentNumber') {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 14);
      let masked = digits;
      if (digits.length <= 11) {
        // CPF: 000.000.000-00
        masked = digits
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      } else {
        // CNPJ: 00.000.000/0000-00
        masked = digits
          .replace(/(\d{2})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1/$2')
          .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
      }
      value = masked;
    }
    // Log para debug das datas
    if (field === 'checkInDate' || field === 'checkOutDate') {
      console.log(` handleInputChange - ${field}:`, value);
      console.log(` handleInputChange - ${field} (Date object):`, new Date(value));
      console.log(` handleInputChange - ${field} (ISO):`, new Date(value).toISOString());
      console.log(` handleInputChange - ${field} (pt-BR):`, new Date(value).toLocaleDateString('pt-BR'));
    }
    
    // Formatao automtica para totalAmount e desconto (moeda)
    if ((field === 'totalAmount' && mode === 'edit') || field === 'discountAmount') {
      value = formatCurrencyInput(value);
    }
    
    // Formatao automtica do telefone
    if (field === 'guestPhone' && value) {
      const numbers = value.replace(/\D/g, '');
      
      if (numbers.length <= 11) {
        if (numbers.length <= 2) {
          value = `(${numbers}`;
        } else if (numbers.length <= 6) {
          value = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        } else if (numbers.length <= 10) {
          value = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        } else {
          value = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
        }
      }
    }
    
    // Limpar erro do campo quando o usurio comear a preencher
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
    
    setFormData(prev => {
      const valueToApply = field === 'childAges'
        ? (Array.isArray(value) ? value : (Array.isArray(prev.childAges) ? prev.childAges : []))
        : value;
      const newFormData = { ...prev, [field]: valueToApply };

      if ((field === 'adults' || field === 'children' || field === 'childAges') && newFormData.roomId) {
        const selectedRoom = availableRooms.find(room => room.id.toString() === newFormData.roomId);
        if (selectedRoom) {
          const adults = Number(newFormData.adults) || 1;
          const childrenCount = Number(newFormData.children) || 0;
          const chargeableChildren = computeChargeableChildren(childrenCount, newFormData.childAges, minChildChargeAge);

          newFormData.totalAmount = computeNightlyPrice({
            singlePrice: selectedRoom.singlePrice,
            couplePrice: selectedRoom.couplePrice,
            adults,
            chargeableChildren,
            childDiscountPercent
          });
        }
      }

      return newFormData;
    });
  };

  // Manter o array de idades alinhado com a quantidade de crianças
  useEffect(() => {
    setFormData(prev => {
      const childrenCount = Number(prev.children) || 0;
      const ages = Array.isArray(prev.childAges) ? prev.childAges.slice(0, childrenCount) : [];
      while (ages.length < childrenCount) ages.push('');
      return { ...prev, childAges: ages };
    });
  }, [formData.children]);

  const handleRoomSelection = async (roomId) => {
    try {
      if (roomId) {
        // No modo de edição, permitimos escolher o quarto original da reserva
        const isOriginalRoom = mode === 'edit' && reservation && String(roomId) === String(reservation.roomId);
        
        // Buscar o quarto em todos os quartos do hotel (availableRooms)
        const selectedRoom = availableRooms.find(room => room.id.toString() === roomId);
        
        if (!selectedRoom) {
          console.log(' handleRoomSelection: Quarto não encontrado:', roomId);
          return;
        }

        // Verificar disponibilidade (apenas se não for o quarto original da reserva)
        if (!isOriginalRoom && !filteredRooms.some(room => room.id.toString() === roomId)) {
          console.log(' handleRoomSelection: Quarto não disponível na lista filtrada:', roomId);
          showNotificationModal('warning', 'Quarto Indisponível', 'Este quarto não está disponível para as datas selecionadas. Tente selecionar outro quarto ou outras datas.');
          return;
        }
        
        console.log(' handleRoomSelection: Quarto selecionado:', selectedRoom);

        const adults = Number(formData.adults) || 1;
        const childrenCount = Number(formData.children) || 0;
        const chargeableChildren = computeChargeableChildren(childrenCount, formData.childAges, minChildChargeAge);

        const calculatedPrice = computeNightlyPrice({
          singlePrice: selectedRoom.singlePrice,
          couplePrice: selectedRoom.couplePrice,
          adults,
          chargeableChildren,
          childDiscountPercent
        });

        setFormData(prev => ({
          ...prev,
          roomId: roomId,
          roomNumber: selectedRoom.number,
          roomType: selectedRoom.type,
          // Apenas atualizamos totalAmount automaticamente no modo create
          totalAmount: mode === 'create' ? calculatedPrice : prev.totalAmount
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          roomId: '',
          roomNumber: '',
          roomType: '',
          totalAmount: 0
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados do quarto:', error);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && (!formData.checkInDate || !formData.checkOutDate || !formData.checkInTime || !formData.checkOutTime)) {
      // Marcar campos de data e horrio como com erro
      setFieldErrors(prev => ({
        ...prev,
        checkInDate: !formData.checkInDate,
        checkInTime: !formData.checkInTime,
        checkOutDate: !formData.checkOutDate,
        checkOutTime: !formData.checkOutTime
      }));
      showNotificationModal('error', 'Datas e Horários Obrigatórios', 'Selecione as datas e horários de check-in e check-out para continuar.');
      return;
    }
    
    if (currentStep === 2 && !formData.roomId) {
      // Marcar campo de quarto como com erro
      setFieldErrors(prev => ({
        ...prev,
        roomId: true
      }));
      showNotificationModal('error', 'Quarto Obrigatório', 'Selecione um quarto para continuar.');
      return;
    }
    
    if (currentStep === 3 && !isPreReservation) {
      // Validar dados do hspede (apenas se não for pré-reserva)
      const missingFields = [];
      if (!formData.guestName) missingFields.push('Nome Completo');
      if (!formData.documentNumber) missingFields.push('CPF/RG');
      
      if (missingFields.length > 0) {
        setFieldErrors(prev => ({
          ...prev,
          guestName: !formData.guestName,
          documentNumber: !formData.documentNumber
        }));
        showNotificationModal('error', 'Campos Obrigatórios', 
          `Os seguintes campos são obrigatórios:\n\n${missingFields.map(field => ` ${field}`).join('\n')}`);
        return;
      }
    }
    
    // Limpar erros ao passar para prxima etapa
    setFieldErrors({});
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    // Limpar erros ao voltar para etapa anterior
    setFieldErrors({});
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const canProceedToNextStep = () => {
    console.log(' canProceedToNextStep - Etapa atual:', currentStep);
    console.log(' canProceedToNextStep - FormData:', formData);
    
    switch (currentStep) {
      case 1:
        const step1Valid = formData.checkInDate && formData.checkOutDate && formData.checkInTime && formData.checkOutTime;
        console.log(' Etapa 1 vlida:', step1Valid, { 
          checkIn: `${formData.checkInDate} ${formData.checkInTime}`, 
          checkOut: `${formData.checkOutDate} ${formData.checkOutTime}` 
        });
        return step1Valid;
      case 2:
        const step2Valid = formData.roomId;
        console.log(' Etapa 2 vlida:', step2Valid, { roomId: formData.roomId });
        return step2Valid;
      case 3:
        // Se for pré-reserva, não precisa validar dados do hóspede
        if (isPreReservation) {
          return true;
        }
        const step3Valid = formData.guestName && formData.documentNumber;
        console.log(' Etapa 3 vlida:', step3Valid, { 
          guestName: formData.guestName, 
          documentNumber: formData.documentNumber 
        });
        return step3Valid;
      case 4:
        // Etapa 4 no tem validao especfica, pode prosseguir
        return true;
      default:
        return true;
    }
  };

  // Funo para formatar datas sem problemas de timezone
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };


  const translateReservationStatus = (status) => {
    const statusMap = {
      'PENDING': 'Pendente',
      'CONFIRMED': 'Confirmada',
      'CHECKED_IN': 'Check-in Realizado',
      'CHECKED_OUT': 'Check-out Realizado',
      'CANCELLED': 'Cancelada'
    };
    return statusMap[status] || status;
  };

  const translatePaymentStatus = (paymentStatus) => {
    const paymentMap = {
      'PENDING': 'Pendente',
      'PAID': 'Pago',
      'OVERDUE': 'Em Atraso',
      'CANCELLED': 'Cancelado',
      'REFUNDED': 'Reembolsado'
    };
    return paymentMap[paymentStatus] || paymentStatus;
  };

  const handleSavePreReservation = async () => {
    try {
      setLoading(true);
      
      // Validar que tem quarto selecionado
      if (!formData.roomId) {
        showNotificationModal('error', 'Quarto Obrigatório', 'Selecione um quarto para criar a pré-reserva.');
        setLoading(false);
        return;
      }
      
      // Validar datas e horários
      const checkInDateTime = new Date(`${formData.checkInDate}T${formData.checkInTime}`);
      const checkOutDateTime = new Date(`${formData.checkOutDate}T${formData.checkOutTime}`);
      
      if (checkOutDateTime <= checkInDateTime) {
        showNotificationModal('error', 'Datas/Horários Inválidos', 'O check-out deve ser posterior ao check-in.');
        setLoading(false);
        return;
      }
      
      // Calcular noites
      const checkInDate = new Date(formData.checkInDate);
      const checkOutDate = new Date(formData.checkOutDate);
      const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
      if (nights < 1) {
        showNotificationModal('error', 'Período Inválido', 'A reserva deve ser para pelo menos 1 noite.');
        setLoading(false);
        return;
      }
      
      // Garantir números válidos
      const sanitizedAdults = Number.isFinite(formData.adults) && formData.adults >= 1 ? formData.adults : 1;
      const sanitizedChildren = Number.isFinite(formData.children) && formData.children >= 0 ? formData.children : 0;
      
      // Calcular valor total
      const totalAmount = formData.totalAmount * nights;
      const discountAmount = 0; // Pré-reserva sem desconto inicial
      
      // Preparar dados da pré-reserva
      const reservationData = {
        ...formData,
        checkInDate: checkInDateTime.toISOString(),
        checkOutDate: checkOutDateTime.toISOString(),
        adults: sanitizedAdults,
        children: sanitizedChildren,
        totalAmount: totalAmount,
        discountAmount: discountAmount,
        hotelId: getHotel()?.id,
        isPreReservation: true
      };
      
      // Enviar apenas dígitos no CPF/CNPJ (se houver)
      reservationData.documentNumber = String(formData.documentNumber || '').replace(/\D/g, '');
      
      // Verificar disponibilidade do quarto
      const selectedRoom = availableRooms.find(r => r.id.toString() === reservationData.roomId);
      if (!selectedRoom) {
        showNotificationModal('error', 'Quarto Inválido', 'O quarto selecionado não foi encontrado.');
        setLoading(false);
        return;
      }
      
      if (!filteredRooms.some(r => r.id.toString() === reservationData.roomId)) {
        showNotificationModal('error', 'Quarto Indisponível', 'O quarto selecionado não está disponível para o período escolhido.');
        setLoading(false);
        return;
      }
      
      // Criar pré-reserva
      const savedReservation = await onSave(reservationData);
      
      // Mostrar link para copiar
      const reservation = savedReservation?.reservation || savedReservation;
      if (reservation?.completionToken) {
        const completionLink = `${window.location.origin}/reservation-completion/${reservation.completionToken}`;
        const hotelName = getHotel()?.name;

        const successDetails = [
          { label: 'Código', value: reservation.reservationCode || 'N/A' },
          { label: 'Quarto', value: reservation.room?.number || reservation.roomNumber || 'N/A' },
          { label: 'Status', value: 'Pré-reserva criada' },
          { label: 'Hotel', value: hotelName || 'Hotel' },
          {
            label: 'Link para completar reserva',
            value: completionLink,
            isCopyable: true,
            guestPhone: reservation.guestPhone || '',
            guestName: reservation.guestName && reservation.guestName !== 'Pré-reserva - Aguardando dados'
              ? reservation.guestName
              : 'Hóspede',
            hotelName
          }
        ];

        showNotificationModal(
          'success',
          'Pré-reserva criada!',
          'Copie o link ou envie pelo WhatsApp para o hóspede completar os dados.',
          successDetails
        );
      } else {
        showNotificationModal(
          'success',
          'Pré-reserva criada!',
          `Pré-reserva criada com sucesso para o quarto ${formData.roomNumber}!`
        );
      }

      // Resetar flag para evitar múltiplas submissões consecutivas
      setIsPreReservation(false);
    } catch (error) {
      console.error('Erro ao criar pré-reserva:', error);
      let errorMessage = 'Ocorreu um erro ao criar a pré-reserva. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      showNotificationModal('error', 'Erro ao Criar Pré-reserva', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {

      // Validar email/telefone opcionais se preenchidos
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\(\d{2}\)\s\d{4,5}-\d{4}$|^\d{10,11}$/;

      if (formData.guestEmail && !emailRegex.test(formData.guestEmail)) {
        showNotificationModal('error', 'E-mail Inválido', 'Corrija o formato do e-mail ou deixe em branco.');
        return;
      }

      if (formData.guestPhone && !phoneRegex.test(formData.guestPhone)) {
        showNotificationModal('error', 'Telefone Inválido', 'Use o formato (11) 99999-9999 ou 11999999999, ou deixe em branco.');
        return;
      }

      // Validar campos obrigatórios (na LP documentNumber não é obrigatório)
      const requiredFields = [
        { key: 'guestName', label: 'Nome Completo' },
        ...(isLanding ? [] : [{ key: 'documentNumber', label: 'CPF/RG' }]),
        { key: 'roomId', label: 'Quarto' },
        { key: 'checkInDate', label: 'Data de Check-in' },
        { key: 'checkOutDate', label: 'Data de Check-out' }
      ];
      
      const missingFields = [];
      const newFieldErrors = {};
      
      // Validar cada campo obrigatório
      requiredFields.forEach(field => {
        const value = formData[field.key];
        let isEmpty = false;
        
        // Validao especfica para cada tipo de campo
        if (field.key === 'roomId') {
          isEmpty = !value || value === '';
        } else if (field.key === 'checkInDate' || field.key === 'checkOutDate') {
          isEmpty = !value || value === '';
        } else {
          // Para campos de texto
          isEmpty = !value || (typeof value === 'string' && value.trim() === '');
        }
        
        if (isEmpty) {
          missingFields.push(field.label);
          newFieldErrors[field.key] = true;
        } else {
          newFieldErrors[field.key] = false;
        }
        
        console.log(` Campo ${field.key} (${field.label}):`, value, 'Vazio:', isEmpty);
      });
      
      // Atualizar estado de erros dos campos IMEDIATAMENTE
      setFieldErrors(newFieldErrors);
      
      console.log(' Validação - Campos obrigatórios:', requiredFields.map(f => f.label));
      console.log(' Validao - Valores atuais:', {
        guestName: formData.guestName,
        originCity: formData.originCity,
        documentNumber: formData.documentNumber,
        roomId: formData.roomId,
        checkInDate: formData.checkInDate,
        checkOutDate: formData.checkOutDate
      });
      console.log(' Validao - Campos faltando:', missingFields);
      console.log(' Campos com erro:', newFieldErrors);
      
      if (missingFields.length > 0) {
        // Criar mensagem de erro mais clara
        let errorMessage;
        if (missingFields.length === 1) {
          errorMessage = `Campo obrigatório não preenchido: ${missingFields[0]}`;
          } else {
          errorMessage = `Campos obrigatórios não preenchidos:\n\n${missingFields.map(field => ` ${field}`).join('\n')}`;
        }
        
        console.log(' Exibindo erro de validao:', errorMessage);
        console.log(' Chamando showNotificationModal com:', { type: 'error', title: 'Campos Obrigatórios', message: errorMessage });
        
        // Mostrar notificao de erro
        showNotificationModal('error', 'Campos Obrigatórios', errorMessage);
        
        // Parar aqui para no continuar com o salvamento
        setLoading(false);
        return;
      }

             // Validar datas e horrios
       const checkInDateTime = new Date(`${formData.checkInDate}T${formData.checkInTime}`);
       const checkOutDateTime = new Date(`${formData.checkOutDate}T${formData.checkOutTime}`);
       
       if (checkOutDateTime <= checkInDateTime) {
         showNotificationModal('error', 'Datas/Horários Inválidos', 'O check-out deve ser posterior ao check-in.');
         return;
       }
       
       // Calcular durao em horas
       const timeDiff = checkOutDateTime.getTime() - checkInDateTime.getTime();
       const hoursDiff = timeDiff / (1000 * 60 * 60);
       
       if (hoursDiff < 1) {
         showNotificationModal('error', 'Período Inválido', 'A reserva deve ser para pelo menos 1 hora.');
         return;
       }
       
       // Calcular noites (para preos)
       const checkInDate = new Date(formData.checkInDate);
       const checkOutDate = new Date(formData.checkOutDate);
       const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
       if (nights < 1) {
         showNotificationModal('error', 'Período Inválido', 'A reserva deve ser para pelo menos 1 noite.');
         return;
       }

      // Garantir números válidos para adultos/crianças
      const sanitizedAdults = Number.isFinite(formData.adults) && formData.adults >= 1
        ? formData.adults
        : 1;
      const sanitizedChildren = Number.isFinite(formData.children) && formData.children >= 0
        ? formData.children
        : 0;

      // Calcular valor total baseado no nmero de noites
      // Salvar o valor ORIGINAL (sem desconto) no totalAmount
      // O desconto ser aplicado apenas na exibio
      // Converter totalAmount e desconto corretamente
      let totalAmountVal = 0;
      if (mode === 'edit') {
        if (formData.totalAmount) {
          if (typeof formData.totalAmount === 'string') {
            totalAmountVal = parseFloat(formData.totalAmount.replace(',', '.')) || 0;
          } else {
            totalAmountVal = Number(formData.totalAmount) || 0;
          }
        }
      } else {
        totalAmountVal = formData.totalAmount * nights;
      }

      let discountAmount = 0;
      if (formData.discountAmount) {
        if (typeof formData.discountAmount === 'string') {
          // Se for string formatada com vírgula (ex: "10,00"), converter para número
          discountAmount = parseFloat(formData.discountAmount.replace(',', '.')) || 0;
        } else {
          discountAmount = Number(formData.discountAmount) || 0;
        }
      }
      
      console.log(' handleSave - Conversions:', {
        formDataTotalAmount: formData.totalAmount,
        formDataDiscountAmount: formData.discountAmount,
        parsedTotalAmount: totalAmountVal,
        parsedDiscountAmount: discountAmount
      });

      // Preparar dados para envio (usar checkInDateTime e checkOutDateTime já declarados acima)
      let reservationData;
      
      if (mode === 'create') {
        reservationData = {
          ...formData,
          checkInDate: checkInDateTime.toISOString(),
          checkOutDate: checkOutDateTime.toISOString(),
          adults: sanitizedAdults,
          children: sanitizedChildren,
          totalAmount: totalAmountVal, // Valor original sem desconto
          discountAmount: discountAmount, // Garantir que o desconto seja enviado corretamente
          hotelId: getHotel()?.id,
          isPreReservation: isPreReservation
        };
      } else {
        reservationData = {
          ...formData,
          checkInDate: checkInDateTime.toISOString(),
          checkOutDate: checkOutDateTime.toISOString(),
          adults: sanitizedAdults,
          children: sanitizedChildren,
          totalAmount: totalAmountVal, // Valor original sem desconto
          discountAmount: discountAmount // Garantir que o desconto seja enviado corretamente
        };
      }
      // Enviar apenas dígitos no CPF/CNPJ
      reservationData.documentNumber = String(formData.documentNumber || '').replace(/\D/g, '');
      
      // Garantir que discountAmount seja enviado explicitamente (pode ser sobrescrito pelo spread)
      reservationData.discountAmount = discountAmount;

      console.log(' handleSave - Dados da reserva a serem enviados:', reservationData);
      console.log(' handleSave - Campos especficos:', {
        totalAmount: reservationData.totalAmount,
        discountAmount: reservationData.discountAmount,
        formDataDiscountAmount: formData.discountAmount
      });
      
      // Verificar se o quarto selecionado est realmente disponvel
      const selectedRoom = availableRooms.find(r => r.id.toString() === reservationData.roomId);
      console.log(' handleSave - Quarto selecionado:', {
        roomId: reservationData.roomId,
        roomData: selectedRoom,
        roomStatus: selectedRoom?.status
      });
      
      if (!selectedRoom) {
        showNotificationModal('error', 'Quarto Inválido', 'O quarto selecionado não foi encontrado. Tente selecionar outro quarto.');
        return;
      }
      
      // Verificação de disponibilidade (na LP o quarto já foi validado na página)
      if (!isLanding) {
        const isOriginalRoom = mode === 'edit' && reservation && String(reservationData.roomId) === String(reservation.roomId);
        if (!isOriginalRoom) {
          const isRoomInFilteredList = filteredRooms.some(r => r.id.toString() === reservationData.roomId);
          console.log(' handleSave - Quarto está na lista filtrada:', isRoomInFilteredList);
          if (!isRoomInFilteredList) {
            showNotificationModal('error', 'Quarto Indisponível para o Período', 'O quarto selecionado não está disponível para o período escolhido. Tente selecionar outro quarto ou outras datas.');
            return;
          }
        }
      }

      const savedReservation = await onSave(reservationData);
      
      // Se for pré-reserva, mostrar link para copiar
      if (isPreReservation) {
        const reservation = savedReservation?.reservation || savedReservation;
        if (reservation?.completionToken) {
          const completionLink = `${window.location.origin}/reservation-completion/${reservation.completionToken}`;
          showNotificationModal('success', 'Pré-reserva Criada', 
            `Pré-reserva criada com sucesso! Envie o link abaixo para o hóspede completar os dados.`,
            [
              { label: 'Link para completar reserva', value: completionLink, guestName: formData.guestName || 'Hóspede', hotelName: getHotel()?.name }
            ]);
        } else {
          showNotificationModal('success', 'Pré-reserva Criada', 
            `Pré-reserva criada com sucesso para o quarto ${formData.roomNumber}!`);
        }
      } else {
        // Notificar sucesso
        const isEdit = mode === 'edit';
        showNotificationModal('success', 
          isEdit ? 'Reserva Atualizada' : 'Reserva Criada', 
          isEdit 
            ? `Reserva atualizada com sucesso para o quarto ${formData.roomNumber || ''}!`
            : `Reserva criada com sucesso para o quarto ${formData.roomNumber || ''}!`
        );
      }
    } catch (error) {
      console.error('Erro ao salvar reserva:', error);
      
      // Extrair mensagem de erro mais especfica
      let errorMessage = 'Ocorreu um erro ao salvar a reserva. Tente novamente.';
      let errorDetails = null;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        
        // Se houver informaes de conflito, adicionar aos detalhes
        if (error.response?.data?.conflict) {
          const conflict = error.response.data.conflict;
          errorDetails = [
            { label: 'Quarto ocupado at', value: conflict.availableAfter }
          ];
          errorMessage = `${errorMessage}. O quarto estará disponível após ${conflict.availableAfter}.`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotificationModal('error', 'Erro ao Salvar', errorMessage, errorDetails);
    } finally {
      setLoading(false);
    }
  };

  const showNotificationModal = (type, title, message, details = null) => {
    console.log(' showNotificationModal chamada com:', { type, title, message, details });
    setNotificationData({ type, title, message, details });
    setShowNotification(true);
    console.log(' showNotificationModal - Estado atualizado:', { type, title, message, details });
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
    // Se foi uma notificação de sucesso, fechar o modal de reserva também (seja criação ou edição)
    if (notificationData.type === 'success') {
      setTimeout(() => {
        onClose();
      }, 100);
    }
  };

  const handleClose = () => {
    // Limpar erros ao fechar o modal
    setFieldErrors({});
    onClose();
  };

  const getStepIcon = (stepNumber) => {
    if (currentStep > stepNumber) {
      return ''; // Completed
    } else if (currentStep === stepNumber) {
      return stepNumber; // Current
    } else {
      return stepNumber; // Pending
    }
  };

  const getStepStatus = (stepNumber) => {
    if (currentStep > stepNumber) {
      return 'completed';
    } else if (currentStep === stepNumber) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  if (!isOpen) return null;

  if (mode === 'view') {
    const getStatusBadgeClass = (status) => {
      switch (status) {
        case 'CONFIRMED':
          return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
        case 'CHECKED_IN':
          return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
        case 'CHECKED_OUT':
          return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
        case 'CANCELLED':
          return 'bg-destructive/10 text-destructive border-destructive/20';
        default:
          return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      }
    };

    const getStatusLabel = (status) => {
      switch (status) {
        case 'CONFIRMED': return 'Confirmada';
        case 'CHECKED_IN': return 'Check-in Realizado';
        case 'CHECKED_OUT': return 'Check-out Realizado';
        case 'CANCELLED': return 'Cancelada';
        default: return 'Pendente';
      }
    };

    const getPaymentStatusBadgeClass = (status) => {
      switch (status) {
        case 'PAID':
          return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
        case 'OVERDUE':
          return 'bg-destructive/10 text-destructive border-destructive/20';
        case 'CANCELLED':
          return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
        case 'REFUNDED':
          return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
        default:
          return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      }
    };

    const getPaymentStatusLabel = (status) => {
      switch (status) {
        case 'PAID': return 'Pago';
        case 'OVERDUE': return 'Atrasado';
        case 'CANCELLED': return 'Cancelado';
        case 'REFUNDED': return 'Reembolsado';
        default: return 'Pendente';
      }
    };

    // Formatar valores para exibição limpa
    const formattedTotal = formData.totalAmount ? `R$ ${formData.totalAmount}` : 'R$ 0,00';
    const formattedDiscount = formData.discountAmount ? `R$ ${formData.discountAmount}` : 'R$ 0,00';

    // Calcular o valor líquido (total - desconto)
    const totalNum = formData.totalAmount ? parseFloat(formData.totalAmount.replace('.', '').replace(',', '.')) : 0;
    const discountNum = formData.discountAmount ? parseFloat(formData.discountAmount.replace('.', '').replace(',', '.')) : 0;
    const netAmount = Math.max(0, totalNum - discountNum);
    const formattedNet = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(netAmount);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4">
        <div className="bg-background rounded-lg shadow-xl max-w-full md:max-w-4xl lg:max-w-5xl w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground flex items-center">
                <Icon name="Eye" className="w-6 h-6 mr-2 text-primary" />
                Detalhes da Reserva
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Visualização completa dos dados da reserva selecionada
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="XCircle" className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 overflow-y-auto flex-1 space-y-6">
            
            {/* Status Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-muted/5 border border-border rounded-lg p-4 flex items-center justify-between shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <Icon name="Info" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Status da Reserva</p>
                    <p className="text-sm font-semibold text-foreground mt-0.5">{getStatusLabel(formData.status)}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeClass(formData.status)}`}>
                  {getStatusLabel(formData.status)}
                </span>
              </div>

              <div className="bg-muted/5 border border-border rounded-lg p-4 flex items-center justify-between shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <Icon name="CreditCard" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Status de Pagamento</p>
                    <p className="text-sm font-semibold text-foreground mt-0.5">{getPaymentStatusLabel(formData.paymentStatus)}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPaymentStatusBadgeClass(formData.paymentStatus)}`}>
                  {getPaymentStatusLabel(formData.paymentStatus)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Coluna 1: Dados do Hóspede */}
              <div className="bg-background border border-border rounded-lg p-5 space-y-4 shadow-sm">
                <h4 className="font-semibold text-foreground flex items-center border-b border-border pb-2">
                  <Icon name="User" className="w-5 h-5 mr-2 text-primary" />
                  Informações do Hóspede
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground font-medium block">Nome Completo</label>
                    <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                      {formData.guestName || 'Não informado'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Telefone</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                        {formData.guestPhone || 'Não informado'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Documento (CPF/RG)</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                        {formData.documentNumber || 'Não informado'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground font-medium block">E-mail</label>
                    <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                      {formData.guestEmail || 'Não informado'}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground font-medium block">Cidade de Origem</label>
                    <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                      {formData.originCity || 'Não informado'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Coluna 2: Dados da Estadia */}
              <div className="bg-background border border-border rounded-lg p-5 space-y-4 shadow-sm">
                <h4 className="font-semibold text-foreground flex items-center border-b border-border pb-2">
                  <Icon name="Calendar" className="w-5 h-5 mr-2 text-primary" />
                  Dados da Estadia
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground font-medium block">Quarto Designado</label>
                    <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50 flex items-center">
                      <Icon name="Home" className="w-4 h-4 mr-2 text-muted-foreground animate-pulse" />
                      {formData.roomNumber ? `${formData.roomNumber} - ${translateRoomType(formData.roomType)}` : 'Não selecionado'}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Data de Check-in</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                        {formData.checkInDate ? formatDateDDMMYYYY(formData.checkInDate) : 'Não informado'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Horário Check-in</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                        {formData.checkInTime || '14:00'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Data de Check-out</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                        {formData.checkOutDate ? formatDateDDMMYYYY(formData.checkOutDate) : 'Não informado'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Horário Check-out</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50">
                        {formData.checkOutTime || '12:00'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Adultos</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50 flex items-center">
                        <Icon name="Users" className="w-4 h-4 mr-2 text-muted-foreground" />
                        {formData.adults || 1}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground font-medium block">Crianças</label>
                      <p className="text-sm font-semibold text-foreground mt-0.5 bg-muted/10 px-3 py-2.5 rounded-lg border border-border/50 flex items-center">
                        <Icon name="Smile" className="w-4 h-4 mr-2 text-muted-foreground" />
                        {formData.children || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção Financeira */}
            <div className="bg-background border border-border rounded-lg p-5 space-y-4 shadow-sm">
              <h4 className="font-semibold text-foreground flex items-center border-b border-border pb-2">
                <Icon name="DollarSign" className="w-5 h-5 mr-2 text-primary" />
                Resumo Financeiro
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-muted/10 p-3 rounded-lg border border-border/50">
                  <label className="text-xs text-muted-foreground font-medium block">Valor de Tabela (Sem Desconto)</label>
                  <p className="text-base font-semibold text-foreground mt-1">{formattedTotal}</p>
                </div>
                <div className="bg-muted/10 p-3 rounded-lg border border-border/50">
                  <label className="text-xs text-muted-foreground font-medium block">Desconto Aplicado</label>
                  <p className="text-base font-semibold text-amber-500 mt-1">{formattedDiscount}</p>
                </div>
                <div className="bg-primary/5 p-3 rounded-lg border border-primary/20">
                  <label className="text-xs text-primary font-semibold block">Valor Líquido (A Pagar)</label>
                  <p className="text-lg font-bold text-primary mt-0.5">{formattedNet}</p>
                </div>
              </div>
            </div>

            {/* Observações / Solicitações Especiais */}
            {formData.specialRequests && (
              <div className="bg-background border border-border rounded-lg p-5 space-y-3 shadow-sm">
                <h4 className="font-semibold text-foreground flex items-center border-b border-border pb-2">
                  <Icon name="FileText" className="w-5 h-5 mr-2 text-primary" />
                  Solicitações Especiais / Observações
                </h4>
                <p className="text-sm text-foreground bg-muted/10 p-3.5 rounded-lg border border-border/50 whitespace-pre-line leading-relaxed">
                  {formData.specialRequests}
                </p>
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-border bg-muted/10">
            <Button variant="outline" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-full md:max-w-4xl lg:max-w-5xl w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
                      <div>
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'create' ? 'Nova Reserva' : 'Editar Reserva'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === 'create' ? 'Crie uma nova reserva em poucos passos' : 'Edite os dados da reserva'}
            </p>
                        </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="XCircle" className="w-5 h-5" />
          </button>
                      </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-background border border-border rounded-lg p-8 shadow-xl">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {mode === 'create' ? 'Criando Reserva...' : 'Salvando Alteraes...'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Por favor, aguarde enquanto processamos sua solicitação.
                  </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
        {/* Stepper */}
        {mode === 'create' && (
          <div className={`px-6 py-4 border-b border-border bg-muted/20 ${loading ? 'opacity-75' : ''}`}>
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${
                    getStepStatus(step.id) === 'completed' 
                      ? 'bg-primary border-primary text-primary-foreground' 
                      : getStepStatus(step.id) === 'current'
                      ? 'border-primary text-primary'
                      : 'border-muted-foreground text-muted-foreground'
                  }`}>
                    {loading && currentStep === 3 ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      getStepIcon(step.id)
                    )}
                  </div>
                  <div className="ml-3">
                    <div className={`text-sm font-medium ${
                      getStepStatus(step.id) === 'current' ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {step.description}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-0.5 mx-4 ${
                      getStepStatus(step.id) === 'completed' ? 'bg-primary' : 'bg-muted-foreground'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`p-4 md:p-6 overflow-y-auto flex-1 min-h-0 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
          {mode === 'edit' ? (
            <div className="space-y-6 max-w-full lg:max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Coluna 1: Dados do Hóspede */}
                <div className="bg-background border border-border rounded-lg p-5 space-y-4">
                  <h4 className="font-semibold text-foreground flex items-center border-b border-border pb-2">
                    <Icon name="User" className="w-5 h-5 mr-2 text-primary" />
                    Informações do Hóspede
                  </h4>
                  <Input
                    label="Nome Completo *"
                    type="text"
                    placeholder="Digite o nome completo"
                    value={formData.guestName}
                    onChange={(e) => handleInputChange('guestName', e.target.value)}
                    required
                    error={fieldErrors.guestName ? 'Nome é obrigatório' : ''}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Telefone *"
                      type="text"
                      placeholder="(11) 99999-9999"
                      value={formData.guestPhone}
                      onChange={(e) => handleInputChange('guestPhone', e.target.value)}
                      required
                    />
                    <Input
                      label="CPF/RG *"
                      type="text"
                      placeholder="Digite o número do documento"
                      value={formData.documentNumber}
                      onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                      required
                      error={fieldErrors.documentNumber ? 'CPF/RG é obrigatório' : ''}
                    />
                  </div>
                  <Input
                    label="E-mail"
                    type="email"
                    placeholder="exemplo@email.com"
                    value={formData.guestEmail}
                    onChange={(e) => handleInputChange('guestEmail', e.target.value)}
                  />
                  <Input
                    label="Cidade de Origem"
                    type="text"
                    placeholder="Digite a cidade de origem"
                    value={formData.originCity}
                    onChange={(e) => handleInputChange('originCity', e.target.value)}
                  />
                </div>

                {/* Coluna 2: Dados da Estadia */}
                <div className="bg-background border border-border rounded-lg p-5 space-y-4">
                  <h4 className="font-semibold text-foreground flex items-center border-b border-border pb-2">
                    <Icon name="Calendar" className="w-5 h-5 mr-2 text-primary" />
                    Dados da Estadia
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Data de Check-in *"
                      type="date"
                      value={formData.checkInDate}
                      onChange={(e) => handleInputChange('checkInDate', e.target.value)}
                      required
                      error={fieldErrors.checkInDate ? 'Data de check-in é obrigatória' : ''}
                    />
                    <Input
                      label="Horário de Check-in *"
                      type="time"
                      value={formData.checkInTime}
                      onChange={(e) => handleInputChange('checkInTime', e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Data de Check-out *"
                      type="date"
                      value={formData.checkOutDate}
                      onChange={(e) => handleInputChange('checkOutDate', e.target.value)}
                      required
                      error={fieldErrors.checkOutDate ? 'Data de check-out é obrigatória' : ''}
                    />
                    <Input
                      label="Horário de Check-out *"
                      type="time"
                      value={formData.checkOutTime}
                      onChange={(e) => handleInputChange('checkOutTime', e.target.value)}
                      required
                    />
                  </div>

                  <Select
                    label="Quarto *"
                    value={formData.roomId}
                    onChange={(val) => handleRoomSelection(val)}
                    options={roomOptions}
                    required
                    error={fieldErrors.roomId ? 'Quarto é obrigatório' : ''}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Adultos"
                      type="number"
                      min="1"
                      value={formData.adults}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : parseInt(e.target.value);
                        handleInputChange('adults', Number.isFinite(v) ? v : 1);
                      }}
                    />
                    <Input
                      label="Crianças"
                      type="number"
                      min="0"
                      value={formData.children}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : parseInt(e.target.value);
                        handleInputChange('children', Number.isFinite(v) ? v : 0);
                      }}
                    />
                  </div>

                  {/* Idades das crianças se houver no modo edit */}
                  {Number(formData.children) > 0 && (
                    <div className="space-y-2 mt-2">
                      <div className="text-xs text-muted-foreground">Idade de cada criança:</div>
                      <div className="grid grid-cols-2 gap-2">
                        {(Array.isArray(formData.childAges) ? formData.childAges : []).map((age, idx) => (
                          <Input
                            key={idx}
                            label={`Criança ${idx + 1}`}
                            type="number"
                            min={0}
                            value={age}
                            onChange={(e) => {
                              const v = e.target.value;
                              const base = Array.isArray(formData.childAges) ? formData.childAges : [];
                              const newAges = [...base.slice(0, idx), v, ...base.slice(idx + 1)];
                              handleInputChange('childAges', newAges);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Seção 3: Valores, Pagamento e Solicitações */}
              <div className="bg-background border border-border rounded-lg p-5 space-y-4">
                <h4 className="font-semibold text-foreground flex items-center border-b border-border pb-2">
                  <Icon name="Receipt" className="w-5 h-5 mr-2 text-primary" />
                  Valores, Status e Observações
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <Input
                    label="Valor Total da Reserva (R$)"
                    type="text"
                    placeholder="0,00"
                    value={formData.totalAmount || ''}
                    onChange={(e) => handleInputChange('totalAmount', e.target.value)}
                    description="Valor total original da reserva"
                  />
                  <Input
                    label="Desconto Aplicado (R$)"
                    type="text"
                    placeholder="0,00"
                    value={formData.discountAmount || ''}
                    onChange={(e) => handleInputChange('discountAmount', e.target.value)}
                    description="Desconto concedido"
                  />
                  <Select
                    label="Status de Pagamento"
                    value={formData.paymentStatus}
                    onChange={(val) => handleInputChange('paymentStatus', val)}
                    options={[
                      { value: 'PENDING', label: 'Pendente' },
                      { value: 'PAID', label: 'Pago' },
                      { value: 'OVERDUE', label: 'Em Atraso' },
                      { value: 'CANCELLED', label: 'Cancelado' },
                      { value: 'REFUNDED', label: 'Reembolsado' }
                    ]}
                  />
                  <Select
                    label="Status da Reserva"
                    value={formData.status}
                    onChange={(val) => handleInputChange('status', val)}
                    options={[
                      { value: 'PENDING', label: 'Pendente' },
                      { value: 'CONFIRMED', label: 'Confirmada' },
                      { value: 'CHECKED_IN', label: 'Check-in Realizado' },
                      { value: 'CHECKED_OUT', label: 'Check-out Realizado' },
                      { value: 'CANCELLED', label: 'Cancelada' }
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Solicitações Especiais / Observações
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Descreva solicitações especiais, observações ou preferências do hóspede..."
                    value={formData.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Step 1: Datas */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Calendar" className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Selecione as Datas da Sua Estadia</h3>
                <p className="text-muted-foreground">Escolha quando você quer fazer check-in e check-out</p>
              </div>

                             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-full md:max-w-2xl mx-auto">
                  {/* Data e Hora de Check-in */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Data de Check-in *</label>
                    {isLanding ? (
                      <input
                        type="text"
                        placeholder="dd/mm/aaaa"
                        className={`w-full px-4 py-3 border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                          fieldErrors.checkInDate ? 'border-destructive' : 'border-border'
                        }`}
                        value={dateDisplayLanding.checkIn !== null ? dateDisplayLanding.checkIn : formatDateDDMMYYYY(formData.checkInDate)}
                        onFocus={() => setDateDisplayLanding((p) => ({ ...p, checkIn: formatDateDDMMYYYY(formData.checkInDate) }))}
                        onChange={(e) => {
                          const masked = maskDateInput(e.target.value);
                          const iso = parseDDMMYYYYToISO(masked);
                          handleInputChange('checkInDate', iso);
                          setDateDisplayLanding((p) => ({ ...p, checkIn: masked }));
                        }}
                        required
                      />
                    ) : (
                      <input
                        type="date"
                        className={`w-full px-4 py-3 border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                          fieldErrors.checkInDate ? 'border-destructive' : 'border-border'
                        }`}
                        value={formData.checkInDate}
                        onChange={(e) => handleInputChange('checkInDate', e.target.value)}
                        required
                      />
                    )}
                    {fieldErrors.checkInDate && (
                      <p className="text-sm text-destructive">Data de check-in  obrigatria</p>
                    )}
                    
                    <label className="text-sm font-medium text-foreground">Horário de Check-in *</label>
                    <input
                      type="time"
                      className={`w-full px-4 py-3 border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                        fieldErrors.checkInTime ? 'border-destructive' : 'border-border'
                      }`}
                      value={formData.checkInTime}
                      onChange={(e) => handleInputChange('checkInTime', e.target.value)}
                      required
                    />
                    {fieldErrors.checkInTime && (
                      <p className="text-sm text-destructive">Horário de check-in é obrigatório</p>
                    )}
                  </div>
                  
                  {/* Data e Hora de Check-out */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Data de Check-out *</label>
                    {isLanding ? (
                      <input
                        type="text"
                        placeholder="dd/mm/aaaa"
                        className={`w-full px-4 py-3 border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                          fieldErrors.checkOutDate ? 'border-destructive' : 'border-border'
                        }`}
                        value={dateDisplayLanding.checkOut !== null ? dateDisplayLanding.checkOut : formatDateDDMMYYYY(formData.checkOutDate)}
                        onFocus={() => setDateDisplayLanding((p) => ({ ...p, checkOut: formatDateDDMMYYYY(formData.checkOutDate) }))}
                        onChange={(e) => {
                          const masked = maskDateInput(e.target.value);
                          const iso = parseDDMMYYYYToISO(masked);
                          handleInputChange('checkOutDate', iso);
                          setDateDisplayLanding((p) => ({ ...p, checkOut: masked }));
                        }}
                        required
                      />
                    ) : (
                      <input
                        type="date"
                        className={`w-full px-4 py-3 border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                          fieldErrors.checkOutDate ? 'border-destructive' : 'border-border'
                        }`}
                        value={formData.checkOutDate}
                        onChange={(e) => handleInputChange('checkOutDate', e.target.value)}
                        min={formData.checkInDate ? new Date(new Date(formData.checkInDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : ''}
                        required
                      />
                    )}
                    {fieldErrors.checkOutDate && (
                      <p className="text-sm text-destructive">Data de check-out  obrigatria</p>
                    )}
                    
                    <label className="text-sm font-medium text-foreground">Horário de Check-out *</label>
                    <input
                      type="time"
                      className={`w-full px-4 py-3 border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                        fieldErrors.checkOutTime ? 'border-destructive' : 'border-border'
                      }`}
                      value={formData.checkOutDate ? 
                        (formData.checkInDate === formData.checkOutDate ? 
                          (formData.checkInTime < '12:00' ? '12:00' : 
                           new Date(`2000-01-01T${formData.checkInTime}`).getTime() + 2 * 60 * 60 * 1000 < new Date('2000-01-01T23:59').getTime() ? 
                           new Date(new Date(`2000-01-01T${formData.checkInTime}`).getTime() + 2 * 60 * 60 * 1000).toTimeString().slice(0, 5) : '23:59') : '12:00') : 
                        '12:00'}
                      onChange={(e) => handleInputChange('checkOutTime', e.target.value)}
                      min={formData.checkInDate === formData.checkOutDate ? 
                        new Date(`2000-01-01T${formData.checkInTime}`).getTime() + 60 * 60 * 1000 < new Date('2000-01-01T23:59').getTime() ? 
                        new Date(new Date(`2000-01-01T${formData.checkInTime}`).getTime() + 60 * 60 * 1000).toTimeString().slice(0, 5) : '23:59' : '00:00'}
                      required
                    />
                    {fieldErrors.checkOutTime && (
                      <p className="text-sm text-destructive">Horário de check-out é obrigatório</p>
                    )}
                  </div>
                </div>
            
                             {formData.checkInDate && formData.checkOutDate && formData.checkInTime && formData.checkOutTime && (
                 <div className="max-w-2xl mx-auto">
                   <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                     <div className="flex items-center justify-between text-sm">
                       <span className="text-muted-foreground">Período selecionado:</span>
                       <span className="font-medium text-foreground">
                         {Math.ceil((new Date(formData.checkOutDate) - new Date(formData.checkInDate)) / (1000 * 60 * 60 * 24))} noite(s)
                       </span>
                     </div>
                     <div className="flex items-center justify-between text-sm mt-2">
                       <span className="text-muted-foreground">Check-in:</span>
                       <span className="font-medium text-foreground">
                         {formatDate(formData.checkInDate)} s {formData.checkInTime}
                       </span>
                     </div>
                     <div className="flex items-center justify-between text-sm">
                       <span className="text-muted-foreground">Check-out:</span>
                       <span className="font-medium text-foreground">
                         {formatDate(formData.checkOutDate)} s {formData.checkOutTime}
                       </span>
                     </div>
                     
                     {/* Duração em horas para reservas de mesmo dia */}
                     {formData.checkInDate === formData.checkOutDate && (
                       <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-primary/20">
                         <span className="text-muted-foreground">Duração:</span>
                         <span className="font-medium text-foreground">
                           {(() => {
                             const checkIn = new Date(`2000-01-01T${formData.checkInTime}`);
                             const checkOut = new Date(`2000-01-01T${formData.checkOutTime}`);
                             const hours = (checkOut - checkIn) / (1000 * 60 * 60);
                             return `${hours} hora${hours > 1 ? 's' : ''}`;
                           })()}
                         </span>
                       </div>
                     )}
                   </div>
                 </div>
               )}
            </div>
          )}

          {/* Step 2: Seleo de Quarto */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Bed" className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Escolha o Quarto Ideal</h3>
                                 <p className="text-muted-foreground">
                   Quartos disponíveis para o período de {formData.checkInDate && formatDate(formData.checkInDate)} às {formData.checkInTime} a {formData.checkOutDate && formatDate(formData.checkOutDate)} às {formData.checkOutTime}
                 </p>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Carregando quartos disponveis...</p>
                </div>
              ) : filteredRooms.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="AlertCircle" className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">
                    {availableRooms.length > 0 
                      ? 'Nenhum quarto disponível para o período selecionado.'
                      : 'Nenhum quarto encontrado para este hotel.'
                    }
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {availableRooms.length > 0 
                      ? 'Tente selecionar outras datas ou clique em " Recarregar Quartos".'
                      : 'Verifique se existem quartos cadastrados para este hotel.'
                    }
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {filteredRooms.map((room) => (
                    <div
                      key={room.id}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        formData.roomId === room.id.toString()
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => handleRoomSelection(room.id.toString())}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-foreground">Quarto {room.number}</h4>
                          <p className="text-sm text-muted-foreground">{translateRoomType(room.type)}</p>
                        </div>
                        <div className={`w-3 h-3 rounded-full ${
                          formData.roomId === room.id.toString() ? 'bg-primary' : 'bg-muted-foreground'
                        }`} />
                      </div>

                      {/* Caractersticas do Quarto */}
                      <div className="flex flex-wrap gap-2 mb-2 items-center">
                        <Icon name="Users" size={14} color="var(--color-primary)" title={`Capacidade: ${room.capacity} pessoas`} />
                        {room.hasAC && <Icon name="Wind" size={14} color="var(--color-primary)" title="Ar Condicionado" />}
                        {room.hasMinibar && <Icon name="Coffee" size={14} color="var(--color-secondary)" title="Frigobar" />}
                        {room.hasBathroom && <Icon name="Bath" size={14} color="var(--color-accent)" title="Banheiro Privativo" />}
                      </div>
                      
                                             <div className="space-y-2 text-sm">
                         <div className="flex justify-between">
                           <span className="text-muted-foreground">Solteiro:</span>
                           <span className="font-medium text-success">R$ {(Number(room.singlePrice) > 0 ? Number(room.singlePrice) : Number(room.couplePrice) || 0)?.toLocaleString()}</span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-muted-foreground">Casal:</span>
                           <span className="font-medium text-success">R$ {room.couplePrice?.toLocaleString()}</span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-muted-foreground">Total estimado:</span>
                           <span className="font-medium text-foreground">
                             R$ {(() => {
                               const adults = Number(formData.adults) || 1;
                               const childrenCount = Number(formData.children) || 0;
                               const chargeableChildren = computeChargeableChildren(childrenCount, formData.childAges, minChildChargeAge);
                               const calculatedPrice = computeNightlyPrice({
                                 singlePrice: room.singlePrice,
                                 couplePrice: room.couplePrice,
                                 adults,
                                 chargeableChildren,
                                 childDiscountPercent
                               });
                               const nights = Math.max(1, Math.ceil((new Date(formData.checkOutDate) - new Date(formData.checkInDate)) / (1000 * 60 * 60 * 24)) || 1);
                               return (calculatedPrice * nights)?.toLocaleString();
                             })()}
                           </span>
                         </div>
                       </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.roomId && (
                <div className="max-w-2xl mx-auto">
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center">
                        <Icon name="Check" className="w-4 h-4 text-success" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">Quarto selecionado!</p>
                        <p className="text-sm text-muted-foreground">
                          {formData.roomNumber} - {translateRoomType(formData.roomType)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
               
               {fieldErrors.roomId && (
                 <div className="max-w-2xl mx-auto">
                   <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                     <div className="flex items-center space-x-3">
                       <div className="w-8 h-8 bg-destructive/20 rounded-full flex items-center justify-center">
                         <Icon name="AlertCircle" className="w-4 h-4 text-destructive" />
                       </div>
                       <div>
                         <p className="font-medium text-foreground">Seleo obrigatria</p>
                         <p className="text-sm text-muted-foreground">
                           Por favor, selecione um quarto para continuar
                         </p>
                       </div>
                     </div>
                   </div>
                 </div>
               )}
            </div>
          )}

          {/* Step 3: Dados do Hspede */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="User" className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Informações do Hóspede</h3>
                <p className="text-muted-foreground">Preencha os dados pessoais do hóspede</p>
              </div>

              <div className="max-w-full lg:max-w-3xl mx-auto">
                <div className="bg-background border border-border rounded-lg p-4 md:p-6">
                  <h4 className="font-medium text-foreground mb-4 flex items-center">
                    <Icon name="User" className="w-4 h-4 mr-2" />
                    Informaes Pessoais
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Obrigatórios */}
                    <Input
                      label="Nome Completo *"
                      type="text"
                      placeholder="Digite o nome completo"
                      value={formData.guestName}
                      onChange={(e) => handleInputChange('guestName', e.target.value)}
                      required
                      error={fieldErrors.guestName ? 'Nome é obrigatório' : ''}
                    />
                    <Input
                      label="Telefone *"
                      type="text"
                      placeholder="(11) 99999-9999"
                      value={formData.guestPhone}
                      onChange={(e) => handleInputChange('guestPhone', e.target.value)}
                      required
                    />
                    <Input
                      label="CPF/RG *"
                      type="text"
                      placeholder="Digite o número do documento"
                      value={formData.documentNumber}
                      onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                      required
                      error={fieldErrors.documentNumber ? 'CPF/RG é obrigatório' : ''}
                    />

                    {/* Não obrigatórios */}
                    <Input
                      label="E-mail"
                      type="email"
                      placeholder="exemplo@email.com"
                      value={formData.guestEmail}
                      onChange={(e) => handleInputChange('guestEmail', e.target.value)}
                    />
                    <Input
                      label="Cidade de Origem"
                      type="text"
                      placeholder="Digite a cidade de origem"
                      value={formData.originCity}
                      onChange={(e) => handleInputChange('originCity', e.target.value)}
                    />
                  </div>
                  {/* Idades das Crianças (se houver) */}
                  {Number(formData.children) > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-foreground mb-3 flex items-center">
                        <Icon name="Users" className="w-4 h-4 mr-2" />
                        Idades das Crianças
                      </h4>
                      <div className="text-xs text-muted-foreground mb-2 space-y-1">
                        <p>Informe a idade de cada criança para o cálculo do valor.</p>
                        {minChildChargeAge != null && Number.isFinite(Number(minChildChargeAge)) && Number(minChildChargeAge) > 0 && (
                          <p>Crianças a partir de {Number(minChildChargeAge) + 1} ano(s) pagam (conforme configuração do hotel).</p>
                        )}
                        {childDiscountPercent != null && Number.isFinite(Number(childDiscountPercent)) && Number(childDiscountPercent) > 0 && (
                          <p>O preço das crianças cobradas utiliza o desconto de {Number(childDiscountPercent)}% sobre o valor solteiro (conforme configuração do hotel).</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {(Array.isArray(formData.childAges) ? formData.childAges : []).map((age, idx) => (
                          <Input
                            key={idx}
                            label={`Criança ${idx + 1}`}
                            type="number"
                            min={0}
                            value={age}
                            onChange={(e) => {
                              const v = e.target.value;
                              const base = Array.isArray(formData.childAges) ? formData.childAges : [];
                              const newAges = [...base.slice(0, idx), v, ...base.slice(idx + 1)];
                              handleInputChange('childAges', newAges);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
            </div>
            </div>
          )}

          {/* Step 4: Detalhes da Reserva */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="text-center mb-3">
                <h3 className="text-base font-semibold text-foreground">Finalizar e confirmar reserva</h3>
                <p className="text-xs text-muted-foreground mt-1">Revise os detalhes antes de confirmar</p>
              </div>

              <div className="max-w-full lg:max-w-4xl mx-auto space-y-4 md:space-y-4">
                {/* Detalhes da Reserva */}
                <div className="bg-background border border-border rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-4 flex items-center">
                    <Icon name="Settings" className="w-4 h-4 mr-2" />
                    Detalhes da Reserva
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                      label="Número de Adultos"
                type="number"
                min="1"
                value={formData.adults}
                onChange={(e) => {
                  const v = e.target.value === '' ? 0 : parseInt(e.target.value);
                  handleInputChange('adults', Number.isFinite(v) ? v : 0);
                }}
              />
              <Input
                      label="Número de Crianças"
                type="number"
                min="0"
                value={formData.children}
                onChange={(e) => {
                  const v = e.target.value === '' ? 0 : parseInt(e.target.value);
                  handleInputChange('children', Number.isFinite(v) ? v : 0);
                }}
              />
                  </div>

                  {/* Idades das Crianças pós seleção */}
                  {Number(formData.children) > 0 && (
                    <div className="mt-3">
                      <div className="text-xs text-muted-foreground mb-2 space-y-1">
                        <p>Informe a idade de cada criança para o cálculo do valor.</p>
                        {minChildChargeAge != null && Number.isFinite(Number(minChildChargeAge)) && Number(minChildChargeAge) > 0 && (
                          <p>Crianças a partir de {Number(minChildChargeAge) + 1} ano(s) pagam (conforme configuração do hotel).</p>
                        )}
                        {childDiscountPercent != null && Number.isFinite(Number(childDiscountPercent)) && Number(childDiscountPercent) > 0 && (
                          <p>O preço das crianças cobradas utiliza o desconto de {Number(childDiscountPercent)}% sobre o valor solteiro (conforme configuração do hotel).</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {(Array.isArray(formData.childAges) ? formData.childAges : []).map((age, idx) => (
                          <Input
                            key={idx}
                            label={`Criança ${idx + 1}`}
                            type="number"
                            min={0}
                            value={age}
                            onChange={(e) => {
                              const v = e.target.value;
                              const base = Array.isArray(formData.childAges) ? formData.childAges : [];
                              const newAges = [...base.slice(0, idx), v, ...base.slice(idx + 1)];
                              handleInputChange('childAges', newAges);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
            </div>
            
                {/* Resumo da Reserva */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-4 flex items-center">
                    <Icon name="Receipt" className="w-4 h-4 mr-2" />
                    Resumo da Reserva
                  </h4>
                  {/* Desconto compacto no resumo (oculto na LP) */}
                  <div className={`grid gap-3 items-end mb-2 ${isLanding ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-4'}`}>
                    <div className={`text-sm text-muted-foreground ${!isLanding ? 'md:col-span-3' : ''}`}>
                      Valores estimados para o período selecionado
                    </div>
                    {!isLanding && (
                      <div>
                        <Input
                          label="Desconto (R$)"
                          type="text"
                          className="h-9"
                          placeholder="0,00"
                          value={formData.discountAmount || ''}
                          onChange={(e) => handleInputChange('discountAmount', e.target.value)}
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Quarto:</span>
                      <div className="font-medium text-foreground">{formData.roomNumber} - {translateRoomType(formData.roomType)}</div>
                  </div>
                    <div>
                      <span className="text-muted-foreground">Hóspedes:</span>
                      <div className="font-medium text-foreground">
                        {formData.adults} adulto{formData.adults > 1 ? 's' : ''}
                        {formData.children > 0 && `, ${formData.children} criança${formData.children > 1 ? 's' : ''}`}
                      </div>
                    </div>
                                         <div>
                       <span className="text-muted-foreground">Período:</span>
                       <div className="font-medium text-foreground">
                         {formData.checkInDate && formData.checkOutDate 
                           ? `${Math.ceil((new Date(formData.checkOutDate) - new Date(formData.checkInDate)) / (1000 * 60 * 60 * 24))} noite${Math.ceil((new Date(formData.checkOutDate) - new Date(formData.checkInDate)) / (1000 * 60 * 60 * 24)) > 1 ? 's' : ''}`
                           : '0 noite'
                         }
                       </div>
                       {formData.checkInDate === formData.checkOutDate && formData.checkInTime && formData.checkOutTime && (
                         <div className="text-xs text-muted-foreground mt-1">
                           {(() => {
                             const checkIn = new Date(`2000-01-01T${formData.checkInTime}`);
                             const checkOut = new Date(`2000-01-01T${formData.checkOutTime}`);
                             const hours = (checkOut - checkIn) / (1000 * 60 * 60);
                             return `${hours} hora${hours > 1 ? 's' : ''}`;
                           })()}
                         </div>
                       )}
                     </div>
                    <div>
                      <span className="text-muted-foreground">Subtotal:</span>
                      <div className="font-medium text-foreground">
                        R$ {formData.checkInDate && formData.checkOutDate && formData.totalAmount > 0 
                          ? (formData.totalAmount * Math.ceil((new Date(formData.checkOutDate) - new Date(formData.checkInDate)) / (1000 * 60 * 60 * 24)))?.toLocaleString()
                          : '0'
                        }
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="flex flex-col gap-2 pt-3 border-t border-primary/20">
                        {(() => {
                          const discountValue = typeof formData.discountAmount === 'string' 
                            ? parseFloat(formData.discountAmount.replace(',', '.')) || 0
                            : Number(formData.discountAmount || 0);
                          return discountValue > 0;
                        })() && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Desconto:</span>
                            <span className="font-medium text-error">
                              - R$ {(() => {
                                const discountValue = typeof formData.discountAmount === 'string' 
                                  ? parseFloat(formData.discountAmount.replace(',', '.')) || 0
                                  : Number(formData.discountAmount || 0);
                                return discountValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              })()}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-semibold">Total a pagar:</span>
                          <div className="font-bold text-lg text-success">
                            R$ {formData.checkInDate && formData.checkOutDate && formData.totalAmount > 0 
                              ? (() => {
                                  const subtotal = formData.totalAmount * Math.ceil((new Date(formData.checkOutDate) - new Date(formData.checkInDate)) / (1000 * 60 * 60 * 24));
                                  // Converter desconto formatado de volta para nmero
                                  const discountValue = typeof formData.discountAmount === 'string' 
                                    ? parseFloat(formData.discountAmount.replace(',', '.')) || 0
                                    : Number(formData.discountAmount || 0);
                                  const total = subtotal - discountValue;
                                  return total > 0 ? total.toLocaleString() : '0';
                                })()
                              : '0'
                            }
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button type="button" className="text-xs text-primary hover:underline" onClick={() => setShowPriceDetails(prev => !prev)}>
                            {showPriceDetails ? 'Ocultar detalhamento' : 'Ver detalhamento'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Detalhamento do preço (colapsável) */}
                   {formData.roomId && formData.totalAmount > 0 && showPriceDetails && (
                    <div className="mt-3 pt-3 border-t border-primary/20">
                       <div className="text-xs text-muted-foreground mb-2">Detalhamento do preço por noite:</div>
                       <div className="space-y-1 text-xs">
                         {(() => {
                           const selectedRoom = availableRooms.find(room => room.id.toString() === formData.roomId);
                           const couplePrice = Number(selectedRoom?.couplePrice) || 0;
                           const singlePrice = Number(selectedRoom?.singlePrice) > 0
                             ? Number(selectedRoom.singlePrice)
                             : couplePrice;
                           const adults = Number(formData.adults) || 0;
                           const childrenCount = Number(formData.children) || 0;
                           const chargeableChildren = computeChargeableChildren(childrenCount, formData.childAges, minChildChargeAge);
                           const childMultiplier = childDiscountPercent != null && Number.isFinite(Number(childDiscountPercent))
                             ? (100 - Number(childDiscountPercent)) / 100
                             : 1;
                           const childSinglePrice = singlePrice * childMultiplier;
                           const totalPeople = adults + chargeableChildren;

                           if (totalPeople <= 1) {
                             return (
                               <div className="flex justify-between">
                                 <span>Preço solteiro (1 pessoa):</span>
                                 <span>R$ {singlePrice?.toLocaleString()}</span>
                               </div>
                             );
                           }

                           if (totalPeople === 2) {
                             if (adults >= 2) {
                               return (
                                 <div className="flex justify-between">
                                   <span>Preço casal (2 pessoas):</span>
                                   <span>R$ {couplePrice?.toLocaleString()}</span>
                                 </div>
                               );
                             }
                             // 1 adulto + 1 criança cobrada
                             return (
                               <>
                                 <div className="flex justify-between">
                                   <span>Preço solteiro (1 adulto):</span>
                                   <span>R$ {singlePrice?.toLocaleString()}</span>
                                 </div>
                                 <div className="flex justify-between text-info">
                                   <span>Criança (1):</span>
                                   <span>+R$ {childSinglePrice?.toLocaleString()}</span>
                                 </div>
                               </>
                             );
                           }

                           // 3+ pessoas
                           const extraAdults = Math.max(0, adults - 2);
                           const extraChildren = chargeableChildren;
                           return (
                             <>
                               {adults >= 2 ? (
                                 <div className="flex justify-between">
                                   <span>Preço casal (2 pessoas):</span>
                                   <span>R$ {couplePrice?.toLocaleString()}</span>
                                 </div>
                               ) : (
                                 <div className="flex justify-between">
                                   <span>Preço solteiro (1 adulto):</span>
                                   <span>R$ {singlePrice?.toLocaleString()}</span>
                                 </div>
                               )}
                               {extraAdults > 0 && (
                                 <div className="flex justify-between text-info">
                                   <span>Adultos extras ({extraAdults}):</span>
                                   <span>+R$ {(extraAdults * singlePrice)?.toLocaleString()}</span>
                                 </div>
                               )}
                               {extraChildren > 0 && (
                                 <div className="flex justify-between text-info">
                                   <span>Crianças cobradas ({extraChildren}):</span>
                                   <span>+R$ {(extraChildren * childSinglePrice)?.toLocaleString()}</span>
                                 </div>
                               )}
                             </>
                           );
                         })()}
                         <div className="flex justify-between font-medium text-foreground pt-1 border-t border-primary/20">
                           <span>Total por noite:</span>
                           <span>R$ {formData.totalAmount?.toLocaleString()}</span>
                         </div>
                       </div>
                     </div>
                   )}
                </div>

                {/* Solicitações Especiais */}
                <div className="bg-background border border-border rounded-lg p-6">
                  <h4 className="font-medium text-foreground mb-4 flex items-center">
                    <Icon name="MessageSquare" className="w-4 h-4 mr-2" />
                Solicitações Especiais
                  </h4>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Descreva qualquer solicitação especial, preferência ou observação..."
                value={formData.specialRequests}
                onChange={(e) => handleInputChange('specialRequests', e.target.value)}
              />
            </div>
          </div>
            </div>
          )}
          </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-3 md:p-6 border-t border-border bg-muted/20 gap-2 sm:gap-3">
          {mode === 'edit' ? (
            <div className="flex items-center justify-end gap-2 w-full">
              <Button 
                variant="default" 
                onClick={() => {
                  setLoading(true);
                  handleSave();
                }}
                disabled={loading}
                size="sm"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Salvando...</span>
                  </div>
                ) : (
                  'Salvar Alterações'
                )}
              </Button>
              <Button variant="outline" onClick={handleClose} disabled={loading} size="sm">
                Cancelar
              </Button>
            </div>
          ) : (
            <>
              {mode === 'create' && (
                <div className="text-xs text-center sm:text-left text-muted-foreground order-2 sm:order-1">
                  {currentStep === 4 
                    ? 'Etapa 4 de 4 - Finalizar e confirmar reserva'
                    : `Etapa ${currentStep} de ${steps.length} - ${steps[currentStep - 1].description}`}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 order-1 sm:order-2">
                {currentStep === 2 && (
                  <div className="flex items-center gap-2 mr-2 text-xs sm:text-sm text-muted-foreground">
                    <span className={`font-medium ${isPreReservation ? 'text-primary' : 'text-muted-foreground'}`}>Pré-Reserva</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPreReservation}
                        onChange={(e) => setIsPreReservation(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-muted-foreground/30 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                )}

                {currentStep > 1 && (
                  <Button variant="outline" onClick={prevStep} size="sm">
                    Anterior
                  </Button>
                )}
                
                {currentStep < 4 ? (
                  <Button 
                    variant="default" 
                    onClick={currentStep === 2 && isPreReservation ? handleSavePreReservation : nextStep}
                    disabled={!canProceedToNextStep() || loading}
                    size="sm"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processando...</span>
                      </div>
                    ) : currentStep === 2 && isPreReservation ? (
                      'Criar Pré-reserva'
                    ) : (
                      'Próximo'
                    )}
                  </Button>
                ) : (
                  <Button 
                    variant="default" 
                    onClick={() => {
                      setLoading(true);
                      handleSave();
                    }}
                    disabled={loading}
                    size="sm"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{mode === 'create' ? 'Criando...' : 'Salvando...'}</span>
                      </div>
                    ) : (
                      mode === 'create' ? 'Criar Reserva' : 'Salvar Alterações'
                    )}
                  </Button>
                )}
                
                <Button variant="outline" onClick={handleClose} disabled={loading} size="sm">
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      
      <NotificationModal
        isOpen={showNotification}
        onClose={handleCloseNotification}
        type={notificationData.type}
        title={notificationData.title}
        message={notificationData.message}
        details={notificationData.details}
      />
    </div>
  );
});

export default ReservationModal;
